import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Container, Skeleton, Stack, Alert, Toolbar, Paper } from '@mui/material';
import { Edit } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import OfferDetailHeader from '../components/OfferDetailHeader';
import PhotoCarousel from '../components/PhotoCarousel';
import OfferInfoSection from '../components/OfferInfoSection';
import PriceSection from '../components/PriceSection';
import LocationAvailabilitySection from '../components/LocationAvailabilitySection';
import QuantitySection from '../components/QuantitySection';
import AttachedFilesSection from '../components/AttachedFilesSection';
import DescriptionSection from '../components/DescriptionSection';
import SendMessageButton from '../components/SendMessageButton';
import ImInterestedButton from '../components/ImInterestedButton';
import ReportOfferButton from '../components/ReportOfferButton';
import { AppButton } from '@/shared/components';
import offerDetailService from '../services/offerDetailService';
import { OfferDetail } from '../types/offer-detail.types';
import { OfferSearchResult } from '../../search/types/search.types';
import { useSearchContext } from '@/shared/contexts/SearchContext';
import { useFavoritesCountContext } from '@/features/favorites/context/FavoritesCountContext';
import { useSnackbar } from '@/shared/hooks/useSnackbar';
import { useParentApp } from '@/shared/hooks/useParentApp';
import { buildOfferShareText, copyToClipboard, fetchShareFile } from '../utils/shareOffer';
import ShareBottomSheet, { ShareChannel } from '../components/ShareBottomSheet';

interface ApiError {
  response?: {
    status?: number;
  };
}

export default function OfferDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const { isInsideIframe, requestShareVia } = useParentApp();
  const { updateFavoriteInResults } = useSearchContext();
  const { refreshCount } = useFavoritesCountContext();

  const [offer, setOffer] = useState<OfferDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareSheetOpen, setShareSheetOpen] = useState(false);
  const [pendingShareText, setPendingShareText] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setError('Offer ID is missing');
      setLoading(false);
      return;
    }

    const loadOffer = async () => {
      try {
        setLoading(true);

        // Check if cached offer data is available from navigation state
        const cachedOffer = location.state?.offerData as OfferSearchResult | undefined;

        if (cachedOffer?.id === id) {
          // Use cached data (already has distance and availabilityDuration from search)
          // But fetch the full offer details to get isFavorite status
          const data = await offerDetailService.getOfferById(id);
          // Merge cached data (has distance/availability) with API data (has isFavorite)
          setOffer({
            ...cachedOffer,
            isFavorite: data.isFavorite,
            alreadyInterested: data.alreadyInterested,
          } as OfferDetail);
          setError(null);
        } else {
          // Fallback to API fetch (deep-link or direct navigation scenario)
          const data = await offerDetailService.getOfferById(id);
          setOffer(data);
          setError(null);
        }
      } catch (err: unknown) {
        console.error('Failed to load offer:', err);
        const apiError = err as ApiError;
        if (apiError.response?.status === 404) {
          setError('Offer not found');
          // Redirect after showing error
          setTimeout(() => {
            navigate('/search', { replace: true });
          }, 3000);
        } else if (apiError.response?.status === 403) {
          setError('You do not have permission to view this offer');
        } else {
          setError('Failed to load offer details');
        }
      } finally {
        setLoading(false);
      }
    };

    loadOffer();
  }, [id, navigate, location.state]);

  const handleFavoriteToggle = () => {
    if (offer && id) {
      const newFavoriteState = !offer.isFavorite;
      setOffer({
        ...offer,
        isFavorite: newFavoriteState,
      });
      // Keep cached search results in sync so the heart icon is correct
      // when navigating back to the Search page.
      updateFavoriteInResults(id, newFavoriteState);
      refreshCount();
    }
  };

  const handleDealClosed = async () => {
    // Refresh offer data to show updated status
    if (id) {
      try {
        const data = await offerDetailService.getOfferById(id);
        setOffer(data);
      } catch (err) {
        console.error('Failed to refresh offer after deal closed:', err);
      }
    }
  };

  const executeShare = async (shareData: ShareData, fallbackText: string) => {
    if ('share' in navigator) {
      try {
        await navigator.share(shareData);
        return;
      } catch (error: unknown) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
      }
    }
    try {
      await copyToClipboard(fallbackText);
      showSnackbar(t('offerDetail.shareCopied'), { variant: 'success' });
    } catch {
      showSnackbar(t('offerDetail.shareCopyFailed'), { variant: 'error' });
    }
  };

  const buildShareText = (): string | null => {
    if (!offer || !id) return null;

    const offerTitle = offer.title || offer.products[0]?.material?.subcategory || 'Untitled Offer';
    const firstProduct = offer.products[0];
    const locationText = offer.address || t('offerDetail.shareLocationFallback');
    const conditionKey = firstProduct?.status === 'NEW' ? 'NEW' : 'USED';
    const conditionLabel = t(`offerDetail.shareCondition.${conditionKey}`);
    const quantityValue = firstProduct?.quantity ?? 0;
    const quantityUnit = firstProduct?.unit?.labelFr || '';
    const quantityLabel = quantityUnit ? `${quantityValue} ${quantityUnit}` : `${quantityValue}`;

    const priceValue = firstProduct?.unitPrice;
    const priceLabel = priceValue != null && priceValue > 0 ? `${priceValue} €` : undefined;

    return buildOfferShareText({
      title: offerTitle,
      location: locationText,
      condition: conditionLabel,
      quantity: quantityLabel,
      price: priceLabel,
      description: offer.description || undefined,
      contactName: offer.owner?.name || undefined,
      contactPhone: offer.owner?.phoneNumber || undefined,
      t,
    });
  };

  const handleShareChannelSelected = async (channel: ShareChannel) => {
    const shareText = pendingShareText ?? buildShareText();
    if (!shareText || !offer) return;

    const offerTitle = offer.title || offer.products[0]?.material?.subcategory || 'Untitled Offer';

    if (channel === 'copy') {
      try {
        const copyText = `📢 Annonce publiée sur Rockease - Bourse aux Matériaux\n${shareText}`;
        await copyToClipboard(copyText);
        showSnackbar(t('offerDetail.shareCopied'), { variant: 'success' });
      } catch {
        showSnackbar(t('offerDetail.shareCopyFailed'), { variant: 'error' });
      }
    } else {
      requestShareVia(channel, { title: offerTitle, text: shareText });
    }

    setPendingShareText(null);
  };

  const handleShare = async () => {
    if (!offer || !id) {
      return;
    }

    if (offer.status === 'CLOSED') {
      showSnackbar(t('offerDetail.shareClosedNotAllowed'), { variant: 'warning' });
      return;
    }

    if (isInsideIframe) {
      const shareText = buildShareText();
      if (shareText) {
        setPendingShareText(shareText);
        setShareSheetOpen(true);
      }
      return;
    }

    try {
      const offerTitle = offer.title || offer.products[0]?.material?.subcategory || 'Untitled Offer';
      const firstProduct = offer.products[0];

      const photoUrl = firstProduct?.photos?.[0]?.url;
      let shareFile: File | null = null;
      try {
        shareFile = photoUrl ? await fetchShareFile(photoUrl) : null;
      } catch {
        // Photo fetch may fail (CORS in webview) — proceed without it
      }

      const shareText = buildShareText()!;
      const shareData: ShareData = { title: offerTitle, text: shareText };
      if (shareFile) shareData.files = [shareFile];
      await executeShare(shareData, shareText);
    } catch (err) {
      console.error('Share failed:', err);
      showSnackbar(t('offerDetail.shareCopyFailed'), { variant: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
        <Toolbar /> {/* Spacer for fixed header */}
        <Container maxWidth="sm" sx={{ pt: 2, pb: 4 }}>
          <Stack spacing={2}>
            <Skeleton variant="rectangular" height={300} />
            <Skeleton variant="text" height={40} />
            <Skeleton variant="text" height={60} />
            <Skeleton variant="text" height={60} />
            <Skeleton variant="rectangular" height={100} />
          </Stack>
        </Container>
      </Box>
    );
  }

  if (error || !offer || !id) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
        <Toolbar />
        <Container maxWidth="sm" sx={{ pt: 4 }}>
          <Alert severity="error">{error || 'Unable to load offer details'}</Alert>
        </Container>
      </Box>
    );
  }

  // Get main photo from first product
  const photos = offer.products[0]?.photos || [];
  const title = offer.title || offer.products[0]?.material?.subcategory || 'Untitled Offer';
  const fromFavorites = (location.state as { from?: string } | undefined)?.from === 'favorites';
  const fromPurchases = (location.state as { from?: string } | undefined)?.from === 'purchases';
  const fromAlerts = (location.state as { from?: string } | undefined)?.from === 'alerts';
  const fromMyPublications =
    (location.state as { from?: string } | undefined)?.from === 'my-publications';
  const isOwnOffer = fromMyPublications;

  const handleBack = () => {
    navigate('/', { replace: true });
  };
  const productName = offer.products[0]?.material?.subcategory || offer.products[0]?.name || '';
  const condition = offer.products[0]?.status || 'USED';
  const unitPrice = offer.products[0]?.unitPrice || 0;
  const quantity = offer.products[0]?.quantity || 0;
  const unit = offer.products[0]?.unit?.labelFr || '';

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <OfferDetailHeader
        offerId={id}
        isFavorite={offer.isFavorite || false}
        onFavoriteToggle={handleFavoriteToggle}
        onShare={handleShare}
        onBack={handleBack}
        isOwner={isOwnOffer}
      />
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Paper
          sx={{
            p: 0,
            mb: 1,
            backgroundColor: 'background.paper',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          }}
          elevation={0}
        >
          <PhotoCarousel photos={photos} />
        </Paper>
        <Paper
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 2,
            backgroundColor: 'background.paper',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          }}
          elevation={0}
        >
          <OfferInfoSection
            title={title}
            productName={productName}
            condition={condition}
            isClosed={offer.status === 'CLOSED'}
            closureReason={offer.closureReason}
          />

          <LocationAvailabilitySection
            address={offer.address}
            distance={offer.distance}
            startDate={offer.startDate}
            endDate={offer.endDate}
            availabilityDuration={offer.availabilityDuration}
            showDateRange={fromFavorites || fromPurchases || fromAlerts || fromMyPublications}
          />

          <QuantitySection quantity={quantity} unit={unit} />

          <PriceSection unitPrice={unitPrice} quantity={quantity} />

          {offer.description && (
            <DescriptionSection
              description={offer.description}
              isOwner={isOwnOffer}
              createdAt={offer.createdAt}
            />
          )}
        </Paper>
        <Paper
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 2,
            backgroundColor: 'background.paper',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          }}
          elevation={0}
        >
          <AttachedFilesSection documents={offer.documents} />
        </Paper>
        {!(isOwnOffer && offer.status === 'CLOSED') && (
          <Paper
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: 'background.paper',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            }}
            elevation={0}
          >
            {isOwnOffer ? (
              /* Owner action bar: Edit + Close (stacked vertically) */
              <Stack direction="column" spacing={1.5}>
                {offer.status !== 'CLOSED' && (
                  <>
                    <AppButton
                      variant="secondary"
                      fullWidth
                      startIcon={<Edit />}
                      onClick={() => navigate(`/offers/${id}/edit`)}
                      data-testid="offer-detail-edit-offer-button"
                    >
                      {t('offerDetail.editOffer')}
                    </AppButton>
                    <ImInterestedButton offer={offer} onDealClosed={handleDealClosed} isOwnerView />
                  </>
                )}
              </Stack>
            ) : (
              /* Buyer action bar: Message + Conclude Deal */
              <>
                <SendMessageButton
                  offerId={id}
                  sellerName={offer.owner?.name}
                  phoneNumber={offer.owner?.phoneNumber}
                />
                <ImInterestedButton offer={offer} onDealClosed={handleDealClosed} />
                <ReportOfferButton offerId={id} />
              </>
            )}
          </Paper>
        )}
      </Container>
      <ShareBottomSheet
        open={shareSheetOpen}
        onClose={() => setShareSheetOpen(false)}
        onSelectChannel={handleShareChannelSelected}
      />
    </Box>
  );
}
